import type {
  FindingAuditLog,
  FindingTimelineDto,
} from '@terra/shared-contracts-findings';

export type RetestCompletedResult = 'success' | 'fail' | null;
type RetestExecution = FindingTimelineDto['retestExecutions'][number];

const TERMINAL_RETEST_EXECUTION_STATUSES = [
  'COMPLETED',
  'FAILED',
  'BLOCKED',
  'ABORTED',
  'STOPPED',
];

const RETEST_STATUS = 'Retest';
const NOT_OBSERVED_OUTCOME = 'NOT_OBSERVED (NO VULNERABILITY FOUND)';
const FAILED_OR_BLOCKED_OUTCOMES = new Set(['FAILED', 'BLOCKED']);

const getAuditLogTime = (log: FindingAuditLog) =>
  new Date(log.createdAt).getTime();

export const getAuditLogMetadataString = ({
  auditLog,
  key,
}: {
  auditLog: Pick<FindingAuditLog, 'metadata'> | null;
  key: string;
}) => {
  const value = auditLog?.metadata?.[key];

  return typeof value === 'string' ? value : null;
};

const getLatestAuditLogByAction = ({
  auditLogs,
  action,
}: {
  auditLogs: FindingAuditLog[];
  action: FindingAuditLog['action'];
}) =>
  auditLogs
    .filter((log) => log.action === action)
    .sort((a, b) => getAuditLogTime(b) - getAuditLogTime(a))[0] ?? null;

const getRetestCompletedResult = ({
  auditLog,
  retestExecution,
}: {
  auditLog: FindingAuditLog | null;
  retestExecution: RetestExecution | null;
}): RetestCompletedResult => {
  const outcome =
    getAuditLogMetadataString({ auditLog, key: 'outcome' }) ??
    retestExecution?.outcome ??
    null;

  if (outcome && FAILED_OR_BLOCKED_OUTCOMES.has(outcome)) {
    return 'fail';
  }

  if (retestExecution?.status === 'COMPLETED') {
    return 'success';
  }

  if (
    retestExecution &&
    TERMINAL_RETEST_EXECUTION_STATUSES.includes(retestExecution.status)
  ) {
    return 'fail';
  }

  if (auditLog) {
    return 'success';
  }

  return null;
};

const getLatestRetestThreadId = (retestThreadIds: string[]) =>
  retestThreadIds.at(-1) ?? null;

const getRetestExecutionByThreadId = ({
  findingTimeline,
  threadId,
}: {
  findingTimeline: FindingTimelineDto | null;
  threadId: string | null;
}) => {
  if (!threadId) {
    return null;
  }

  return (
    findingTimeline?.retestExecutions.find(
      (execution) => execution.threadId === threadId,
    ) ?? null
  );
};

const isTerminalRetestExecution = (retestExecution: RetestExecution | null) =>
  Boolean(
    retestExecution &&
      TERMINAL_RETEST_EXECUTION_STATUSES.includes(retestExecution.status),
  );

export const getFindingRetestUiState = ({
  findingStatus,
  auditLogs,
  findingTimeline,
  retestThreadIds = [],
  isRequestingRetest = false,
  scheduledRetestAt,
  skipHumanValidation = false,
}: {
  findingStatus?: string | null;
  auditLogs: FindingAuditLog[];
  findingTimeline: FindingTimelineDto | null;
  retestThreadIds?: string[];
  isRequestingRetest?: boolean;
  scheduledRetestAt?: Date | string | null;
  skipHumanValidation?: boolean;
}) => {
  const latestRetestCompletedAuditLog = getLatestAuditLogByAction({
    auditLogs,
    action: 'RETEST_COMPLETED',
  });
  const latestRetestAbortedAuditLog = getLatestAuditLogByAction({
    auditLogs,
    action: 'RETEST_ABORTED',
  });
  const latestRetestStartedAuditLog = getLatestAuditLogByAction({
    auditLogs,
    action: 'RETEST_STARTED',
  });
  const latestRetestThreadId = getLatestRetestThreadId(retestThreadIds);
  const latestRetestExecution = getRetestExecutionByThreadId({
    findingTimeline,
    threadId: latestRetestThreadId,
  });
  const latestRetestCompletedThreadId = getAuditLogMetadataString({
    auditLog: latestRetestCompletedAuditLog,
    key: 'threadId',
  });
  const latestRetestAbortedThreadId = getAuditLogMetadataString({
    auditLog: latestRetestAbortedAuditLog,
    key: 'threadId',
  });
  const latestRetestAbortCancelledBeforeStart =
    latestRetestAbortedAuditLog?.metadata?.cancelledBeforeStart === true;
  const hasRetestStartedAfterAbort =
    !!latestRetestAbortedAuditLog &&
    !!latestRetestStartedAuditLog &&
    getAuditLogTime(latestRetestStartedAuditLog) >
      getAuditLogTime(latestRetestAbortedAuditLog);
  const hasLatestRetestCompletionAudit =
    !!latestRetestCompletedAuditLog &&
    (!latestRetestThreadId ||
      latestRetestCompletedThreadId === latestRetestThreadId);
  const hasLatestRetestAbortAudit =
    !!latestRetestAbortedAuditLog &&
    !hasRetestStartedAfterAbort &&
    (latestRetestAbortCancelledBeforeStart ||
      !latestRetestThreadId ||
      latestRetestAbortedThreadId === latestRetestThreadId);
  const isLatestRetestExecutionTerminal = isTerminalRetestExecution(
    latestRetestExecution,
  );
  const hasScheduledRetest = Boolean(scheduledRetestAt);
  const isRetesting =
    isRequestingRetest ||
    (!hasScheduledRetest &&
      findingStatus === RETEST_STATUS &&
      Boolean(latestRetestThreadId) &&
      !hasLatestRetestCompletionAudit &&
      !hasLatestRetestAbortAudit &&
      (!latestRetestExecution || !isLatestRetestExecutionTerminal));
  const completionAuditLog = hasLatestRetestCompletionAudit
    ? latestRetestCompletedAuditLog
    : null;
  const retestCompletedResult =
    findingStatus === RETEST_STATUS &&
    !isRetesting &&
    !hasScheduledRetest &&
    !hasLatestRetestAbortAudit
      ? getRetestCompletedResult({
          auditLog: completionAuditLog,
          retestExecution: latestRetestExecution,
        })
      : null;
  const latestRetestOutcome =
    getAuditLogMetadataString({
      auditLog: completionAuditLog,
      key: 'outcome',
    }) ??
    latestRetestExecution?.outcome ??
    null;

  return {
    isRetesting,
    retestCompletedResult,
    isPendingHumanValidation:
      !skipHumanValidation &&
      retestCompletedResult === 'success' &&
      latestRetestOutcome === NOT_OBSERVED_OUTCOME,
    activeRetestThreadId:
      isRequestingRetest || hasScheduledRetest ? null : latestRetestThreadId,
    activeRetestTraceId:
      isRequestingRetest || hasScheduledRetest
        ? null
        : (latestRetestExecution?.id ?? null),
    activeRetestExecutionId:
      isRequestingRetest || hasScheduledRetest
        ? null
        : (latestRetestExecution?.executionId ?? null),
  };
};

export const shouldDisplayRetestingChip = ({
  isRetesting,
  isAbortInFlight = false,
  isShowingAbortedChip = false,
  hasLocalAbort = false,
}: {
  isRetesting: boolean;
  isAbortInFlight?: boolean;
  isShowingAbortedChip?: boolean;
  hasLocalAbort?: boolean;
}) =>
  (isRetesting || isAbortInFlight) && !isShowingAbortedChip && !hasLocalAbort;

export const canRequestRetestForFindingStatus = ({
  findingStatus,
}: {
  findingStatus?: string | null;
}) => findingStatus !== RETEST_STATUS;
